import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ArrowPathIcon, InformationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import { LoaderSpinner } from "../LoaderSpinner";
import { arrayHasItems } from "../../helpers/common";
import {
  addReportSheetDestination,
  getReportSheetSync,
  removeReportSheetDestination,
  runReportSheetSync,
  updateReportSheetDestination,
} from "../../slices/reportSheetSync";

const TIME_ZONE = 'America/Denver';
const HISTORY_PAGE_SIZE = 5;

const formatDateTime = (value) => {
  const date = new Date(value);
  if (isNaN(date)) return { day: '', time: '' };
  return {
    day: date.toLocaleDateString('en-US', { timeZone: TIME_ZONE, month: '2-digit', day: '2-digit', year: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' }),
  };
};

const toastError = (error, fallback) => {
  const message = typeof error === 'string' ? error : error?.error || fallback;
  toast.error(typeof message === 'string' ? message : fallback, { position: 'bottom-right' });
};

/**
 * "Google Sheets" section of the report settings sidebar: link any number of
 * spreadsheet tabs to a scheduled report. Each scheduled run (and "Sync now")
 * replaces the tab contents with the report rows, keeping the tab itself.
 */
export const ReportSheetSyncSettings = ({ reportType, handlerParams = {} }) => {
  const dispatch = useDispatch();
  const entry = useSelector((state) => state.reportSheetSync?.byReport?.[reportType]);
  const isLoading = useSelector((state) => state.reportSheetSync?.loading);
  const isSyncing = useSelector((state) => state.reportSheetSync?.syncing);

  const destinations = useMemo(() => entry?.destinations || [], [entry]);
  const history = useMemo(() => entry?.history || [], [entry]);
  const columnOptions = useMemo(() => entry?.columns || [], [entry]);
  const defaultColumnKeys = useMemo(
    () => columnOptions.filter((column) => column.isDefault).map((column) => column.key),
    [columnOptions],
  );

  const [spreadsheet, setSpreadsheet] = useState('');
  const [tabName, setTabName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [busyDestinationId, setBusyDestinationId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    setSelectedColumns(defaultColumnKeys);
  }, [defaultColumnKeys]);

  useEffect(() => {
    setHistoryPage(1);
  }, [history]);

  const usesDefaultColumns = selectedColumns.length === defaultColumnKeys.length
    && selectedColumns.every((key, index) => key === defaultColumnKeys[index]);

  const toggleColumn = (key) => {
    setSelectedColumns((prev) => (prev.includes(key)
      ? prev.filter((k) => k !== key)
      : [...columnOptions.map((c) => c.key).filter((k) => k === key || prev.includes(k))]));
  };

  const handleAdd = async () => {
    const link = spreadsheet.trim();
    if (!link) {
      toast.error('Paste a Google Sheets link or spreadsheet ID', { position: 'bottom-right' });
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error('Select at least one column', { position: 'bottom-right' });
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(addReportSheetDestination({
        reportType,
        spreadsheet: link,
        tabName: tabName.trim(),
        columns: usesDefaultColumns ? null : selectedColumns,
      })).unwrap();
      toast.success('Google Sheet linked', { position: 'bottom-right' });
      setSpreadsheet('');
      setTabName('');
      setSelectedColumns(defaultColumnKeys);
      setShowColumnPicker(false);
    } catch (error) {
      toastError(error, 'Failed to link Google Sheet');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggle = async (destination) => {
    setBusyDestinationId(destination.id);
    try {
      await dispatch(updateReportSheetDestination({
        reportType,
        destinationId: destination.id,
        enabled: !destination.enabled,
      })).unwrap();
    } catch (error) {
      toastError(error, 'Failed to update destination');
    } finally {
      setBusyDestinationId(null);
    }
  };

  const handleRemove = async (destination) => {
    if (!window.confirm(`Unlink ${destination.spreadsheetTitle || 'this spreadsheet'} / ${destination.tabName}? The sheet itself is not changed.`)) return;
    setBusyDestinationId(destination.id);
    try {
      await dispatch(removeReportSheetDestination({ reportType, destinationId: destination.id })).unwrap();
      toast.success('Google Sheet unlinked', { position: 'bottom-right' });
    } catch (error) {
      toastError(error, 'Failed to unlink Google Sheet');
    } finally {
      setBusyDestinationId(null);
    }
  };

  const handleSync = async (destination = null) => {
    setBusyDestinationId(destination?.id || 'all');
    try {
      const { data } = await dispatch(runReportSheetSync({
        reportType,
        destinationId: destination?.id || null,
        handlerParams,
      })).unwrap();
      const results = data?.results || [];
      const failed = results.filter((result) => result.status === 'ERROR');
      if (failed.length > 0) {
        toast.error(data?.error || 'Some destinations failed to sync', { position: 'bottom-right' });
      } else {
        const total = results.reduce((sum, result) => sum + (result.rowsWritten || 0), 0);
        toast.success(`Synced ${results.length} sheet${results.length === 1 ? '' : 's'} (${total} rows)`, { position: 'bottom-right' });
      }
      dispatch(getReportSheetSync(reportType));
    } catch (error) {
      toastError(error, 'Google Sheets sync failed');
      dispatch(getReportSheetSync(reportType));
    } finally {
      setBusyDestinationId(null);
    }
  };

  const totalHistoryPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const paginatedHistory = history.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);
  const destinationById = useMemo(() => new Map(destinations.map((d) => [d.id, d])), [destinations]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="block text-base font-medium text-gray-900">Google Sheets</label>
        {arrayHasItems(destinations) && (
          <button
            type="button"
            disabled={isSyncing || !entry?.isConfigured}
            onClick={() => handleSync(null)}
            className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 ${busyDestinationId === 'all' ? 'animate-spin' : ''}`} />
            Sync all now
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Each scheduled run replaces the contents of the linked tab(s) with this report (same as Import → Replace current sheet). The tab is kept, so formulas pointing at it keep working.
      </p>
      {entry && !entry.isConfigured && (
        <p className="text-xs text-red-500">Google Sheets is not configured on the server (GOOGLE_SERVICE_ACCOUNT_JSON). Ask an admin to add the service account.</p>
      )}
      {entry?.serviceAccountEmail && (
        <p className="text-xs text-gray-600">
          Share each spreadsheet with <span className="font-mono select-all">{entry.serviceAccountEmail}</span> as Editor.
        </p>
      )}

      {isLoading && !entry && <LoaderSpinner width="30" height="30" border="3" colorSpinner="black" />}

      {arrayHasItems(destinations) && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm sidebar-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spreadsheet / Tab</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[90px]">Columns</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">Enabled</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[90px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {destinations.map((destination) => {
                const busy = busyDestinationId === destination.id;
                return (
                  <tr key={destination.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-xs align-middle">
                      <a href={destination.spreadsheetUrl} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                        {destination.spreadsheetTitle || destination.spreadsheetId}
                      </a>
                      <div className="text-gray-500">{destination.tabName}</div>
                    </td>
                    <td className="px-3 py-2 text-xs align-middle text-gray-600">
                      {destination.columns ? `${destination.columns.length} selected` : 'Default'}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={destination.enabled}
                        disabled={busy}
                        onClick={() => handleToggle(destination)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-60 ${destination.enabled ? 'bg-black' : 'bg-gray-300'}`}
                      >
                        <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${destination.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Sync now"
                          disabled={busy || isSyncing || !entry?.isConfigured}
                          onClick={() => handleSync(destination)}
                          className="p-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                        >
                          <ArrowPathIcon className={`h-4 w-4 ${busy && isSyncing ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          type="button"
                          title="Unlink"
                          disabled={busy}
                          onClick={() => handleRemove(destination)}
                          className="p-1 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
        <span className="text-sm font-medium text-gray-900">Link a spreadsheet tab</span>
        <input
          type="text"
          value={spreadsheet}
          onChange={(e) => setSpreadsheet(e.target.value)}
          placeholder="Paste the Google Sheets link (tab URL with #gid=… works too)"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <input
          type="text"
          value={tabName}
          onChange={(e) => setTabName(e.target.value)}
          placeholder="Tab name (optional if the link includes #gid=…)"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {arrayHasItems(columnOptions) && (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setShowColumnPicker((v) => !v)}
              className="self-start text-xs text-primary underline"
            >
              {showColumnPicker ? 'Hide columns' : `Columns: ${usesDefaultColumns ? 'default' : `${selectedColumns.length} selected`}`}
            </button>
            {showColumnPicker && (
              <div className="grid grid-cols-2 gap-1 rounded-md border border-gray-200 bg-white p-2 max-h-48 overflow-y-auto">
                {columnOptions.map((column) => (
                  <label key={column.key} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(column.key)}
                      onChange={() => toggleColumn(column.key)}
                      className="h-3.5 w-3.5 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    {column.label}
                  </label>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedColumns(defaultColumnKeys)}
                  className="col-span-2 self-start text-xs text-gray-500 underline"
                >
                  Reset to default
                </button>
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          disabled={isAdding || !entry?.isConfigured}
          onClick={handleAdd}
          className="self-start transition-all duration-300 bg-primary cursor-pointer text-white px-4 py-2 rounded-md hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAdding ? 'Checking access…' : 'Link sheet'}
        </button>
      </div>

      {arrayHasItems(history) && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-900">Sync History</span>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm sidebar-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[130px]">Synced At</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tab</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[90px]">Trigger</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[70px]">Rows</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedHistory.map((item) => {
                  const { day, time } = formatDateTime(item.createdAt);
                  const destination = destinationById.get(item.destinationId);
                  return (
                    <tr key={item.id} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 text-xs whitespace-nowrap align-middle">
                        <div>{day}</div>
                        <div className="text-gray-500">{time}</div>
                      </td>
                      <td className="px-3 py-2 text-xs align-middle">
                        <div className="truncate max-w-[180px]">{destination?.spreadsheetTitle || item.spreadsheetId}</div>
                        <div className="text-gray-500">{item.tabName}</div>
                      </td>
                      <td className="px-3 py-2 text-xs align-middle">
                        <span className={`px-2 py-1 rounded-lg text-white ${item.trigger === 'MANUAL' ? 'bg-blue-500' : 'bg-primary'}`}>
                          {item.trigger === 'MANUAL' ? 'Manual' : 'Scheduled'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs align-middle">{item.rowsWritten ?? ''}</td>
                      <td className="px-3 py-2 text-xs align-middle">
                        <div className="flex items-center gap-2">
                          <div className={`text-white w-14 text-center px-2 py-1 rounded-md ${item.status === 'SUCCESS' ? 'bg-primary' : 'bg-red-500'}`}>
                            {item.status === 'SUCCESS' ? 'Synced' : 'Failed'}
                          </div>
                          {item.error && (
                            <div className="flex items-center">
                              <InformationCircleIcon className="h-4 w-4 text-red-500" data-tip data-for={`sheet-sync-err-${item.id}`} />
                              <ReactTooltip id={`sheet-sync-err-${item.id}`} effect="solid" place="top">
                                <div className="w-64 whitespace-normal break-words">{item.error}</div>
                              </ReactTooltip>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalHistoryPages > 1 && (
              <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-white">
                <span className="text-xs text-gray-500">Page {historyPage} of {totalHistoryPages}</span>
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50" disabled={historyPage === 1} onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}>Prev</button>
                  <button className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50" disabled={historyPage === totalHistoryPages} onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}>Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSheetSyncSettings;
